import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Download,
  Calendar as CalendarIcon,
  Clock,
  MoreHorizontal,
  User,
  MapPin,
  Globe,
  ChevronRight
} from 'lucide-react';

const API_BASE_URL = 'https://faas-sgp1-18bc02ac.doserverless.co/api/v1/web/fn-eabd0529-e853-4bfe-85de-7e0fb90bd150/bookings';

const getAccessToken = () => sessionStorage.getItem('access_token');

const fetchAuth0Users = () => {
  const userMap = new Map();

  try {
    const storedUser = JSON.parse(sessionStorage.getItem('user') || '{}');

    if (storedUser.email) {
      const username =
        storedUser.username ||
        storedUser.name ||
        storedUser.email.split('@')[0];

      userMap.set(storedUser.email, username);
    }
  } catch (e) {
    console.error('Error reading user from sessionStorage:', e);
  }

  return userMap;
};

function CancelledBookingRow({
  booking,
  isSelected,
  onSelect,
  onNavigateToDetail,
  onRefresh
}) {
  const [showActions, setShowActions] = useState(false);

  const handleReactivate = async (e) => {
    e?.stopPropagation();

    if (!confirm('Are you sure you want to reactivate this booking?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/updateBooking`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAccessToken()}`
        },
        body: JSON.stringify({
          booking_id: booking.id,
          is_canceled: false
        })
      });

      if (!response.ok) {
        throw new Error('Failed to reactivate booking');
      }

      onRefresh?.();
    } catch (error) {
      console.error('Error reactivating booking:', error);
      alert(`Failed to reactivate booking: ${error.message}`);
    }
  };

  const handleDelete = async (e) => {
    e?.stopPropagation();

    if (
      !confirm(
        'Are you sure you want to permanently delete this booking?\n\nThis action cannot be undone.'
      )
    ) {
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/deleteBooking/${booking.id}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getAccessToken()}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete booking');
      }

      onRefresh?.();
    } catch (error) {
      console.error('Error deleting booking:', error);
      alert(`Failed to delete booking: ${error.message}`);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';

    const date = new Date(dateString);

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'Asia/Jakarta'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';

    const date = new Date(dateString);

    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Asia/Jakarta'
    });
  };

  const calculateDuration = (start, end) => {
    if (!start || !end) return '-';

    const diff = new Date(end) - new Date(start);

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor(
      (diff % (1000 * 60 * 60)) / (1000 * 60)
    );

    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  };

  return (
    <div
      className="flex items-center px-4 py-4 border-b hover:bg-gray-50 cursor-pointer transition-colors duration-150"
      style={{
        borderColor: '#D3DAE6',
        minHeight: '89px'
      }}
      onClick={() => onNavigateToDetail(booking.id)}
      role="button"
      tabIndex={0}
    >
      <div className="w-8 mr-4">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onSelect(booking.id)}
          onClick={(e) => e.stopPropagation()}
          className="w-4 h-4 rounded border-gray-300"
          style={{ accentColor: '#184AC0' }}
        />
      </div>

      <div className="w-20 h-16 mr-4 flex-shrink-0">
        <div className="w-full h-full bg-gray-200 rounded-md flex items-center justify-center overflow-hidden">
          {booking.resource?.metadata?.photo_url ? (
            <img
              src={booking.resource.metadata.photo_url}
              alt={booking.resource?.name || 'Resource'}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextElementSibling.style.display = 'flex';
              }}
            />
          ) : null}

          <div
            className="w-12 h-12 bg-gray-300 rounded flex items-center justify-center text-gray-600 text-xs"
            style={{
              display: booking.resource?.metadata?.photo_url
                ? 'none'
                : 'flex'
            }}
          >
            Room
          </div>
        </div>
      </div>

      <div className="flex-1 mr-4 min-w-0">
        <h3 className="text-sm font-semibold text-gray-900 truncate mb-1">
          {booking.resource?.name || 'Unknown'} -{' '}
          {booking.service?.name || 'Unknown'}
        </h3>

        <div className="flex items-center gap-1 mb-1">
          <CalendarIcon className="w-3 h-3 text-gray-400" />

          <p className="text-xs text-gray-500">
            {formatDate(booking.starts_at)}
          </p>

          <Clock className="w-3 h-3 text-gray-400 ml-2" />

          <p className="text-xs text-gray-500">
            {`${formatTime(booking.starts_at)} - ${formatTime(
              booking.ends_at
            )}`}
          </p>
        </div>

        <div className="flex items-center gap-4">
          {booking.location && (
            <div className="flex items-center gap-1">
              <MapPin className="w-3 h-3 text-gray-400" />

              <p className="text-xs text-gray-400">
                {booking.location.name || 'Unknown'}
              </p>

              <Globe className="w-3 h-3 text-gray-400 ml-1" />

              <p className="text-xs text-gray-400">
                {booking.location.time_zone || 'UTC'}
              </p>
            </div>
          )}

          <div className="flex items-center gap-1">
            <User className="w-3 h-3 text-gray-400" />

            <p className="text-xs text-gray-400">
              {booking.metadata?.customer_name ||
                booking.customer_name ||
                booking.customer ||
                'Unknown'}
            </p>
          </div>
        </div>
      </div>

      <div className="w-32 mr-4 text-sm text-gray-500">
        {booking.price > 0
          ? `IDR ${parseFloat(booking.price).toFixed(2)}`
          : '-'}
      </div>

      <div className="w-24 mr-4 text-sm text-gray-500">
        {calculateDuration(
          booking.starts_at,
          booking.ends_at
        )}
      </div>

      <div className="w-24 mr-4">
        <span
          className="inline-flex px-2 py-1 text-xs font-medium rounded-full"
          style={{
            backgroundColor: '#db6969',
            color: 'white'
          }}
        >
          Cancelled
        </span>
      </div>

      <div className="w-8 relative">
        <button
          className="p-1 hover:bg-gray-200 rounded"
          onClick={(e) => {
            e.stopPropagation();
            setShowActions(!showActions);
          }}
        >
          <MoreHorizontal className="w-4 h-4 text-gray-500" />
        </button>

        {showActions && (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={() => setShowActions(false)}
            />

            <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-20 min-w-40">
              <button
                className="block w-full text-left px-4 py-2 text-sm text-green-600 hover:bg-green-50"
                onClick={handleReactivate}
              >
                Reactivate
              </button>

              <button
                className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
                style={{ color: '#db6969' }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#FEE2E2';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '';
                }}
                onClick={handleDelete}
              >
                Delete
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function CancelledBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBookings, setSelectedBookings] = useState([]);
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(100);
  const [totalPages, setTotalPages] = useState(1);
  const [totalBookings, setTotalBookings] = useState(0);

  const [showBulkActions, setShowBulkActions] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    fetchCancelledBookings(currentPage);
  }, [currentPage]);

  const fetchCancelledBookings = async (page = currentPage) => {
    try {
      setLoading(true);
      setError(null);

      const userMap = await fetchAuth0Users();

      const response = await fetch(
        `${API_BASE_URL}/viewAllcancelledBookings?page=${page}&limit=${pageSize}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getAccessToken()}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch cancelled bookings');
      }

      const result = await response.json();

      console.log('Cancelled bookings API response:', result);

      const cancelledBookings = (result.data || []).map((booking) => {
        let email =
          booking.customer_id ||
          booking.customer_name ||
          booking.metadata?.customer_name;

        if (
          booking.customer_name &&
          booking.customer_name.includes('@')
        ) {
          email = booking.customer_name;
        } else if (
          booking.metadata?.customer_name &&
          booking.metadata.customer_name.includes('@')
        ) {
          email = booking.metadata.customer_name;
        }

        const properUsername =
          email && userMap.has(email)
            ? userMap.get(email)
            : (
                booking.metadata?.customer_name ||
                booking.customer_name ||
                booking.customer ||
                'Unknown User'
              );

        return {
          ...booking,
          customer_name: properUsername,
          customer: properUsername
        };
      });

      setBookings(cancelledBookings);

      setTotalBookings(result.meta?.total || 0);
      setTotalPages(result.meta?.totalPages || 1);
    } catch (err) {
      setError('Failed to fetch cancelled bookings');
      console.error(
        'Error fetching cancelled bookings:',
        err
      );
    } finally {
      setLoading(false);
    }
  };

  const getFilteredBookings = () => {
    if (!searchTerm) {
      return bookings;
    }

    const search = searchTerm.toLowerCase();

    return bookings.filter((booking) =>
      (booking.resource?.name || '')
        .toLowerCase()
        .includes(search) ||
      (booking.service?.name || '')
        .toLowerCase()
        .includes(search) ||
      (
        booking.metadata?.customer_name ||
        booking.customer_name ||
        booking.customer ||
        ''
      )
        .toLowerCase()
        .includes(search) ||
      (booking.id || '')
        .toLowerCase()
        .includes(search)
    );
  };

  const getSortedBookings = (bookingsToSort) => {
    return [...bookingsToSort].sort((a, b) => {
      let aValue;
      let bValue;

      switch (sortBy) {
        case 'resource':
          aValue = (a.resource?.name || '').toLowerCase();
          bValue = (b.resource?.name || '').toLowerCase();
          break;

        case 'duration':
          aValue =
            a.ends_at && a.starts_at
              ? new Date(a.ends_at) - new Date(a.starts_at)
              : 0;

          bValue =
            b.ends_at && b.starts_at
              ? new Date(b.ends_at) - new Date(b.starts_at)
              : 0;
          break;

        case 'date':
        default:
          aValue = a.starts_at
            ? new Date(a.starts_at)
            : new Date(0);

          bValue = b.starts_at
            ? new Date(b.starts_at)
            : new Date(0);

          break;
      }

      return sortOrder === 'asc'
        ? aValue > bValue
          ? 1
          : -1
        : aValue < bValue
        ? 1
        : -1;
    });
  };

  const filteredBookings = getFilteredBookings();
  const sortedBookings = getSortedBookings(filteredBookings);
  const paginatedBookings = sortedBookings;

  const handleExport = () => {
    if (sortedBookings.length === 0) {
      alert('No bookings to export');
      return;
    }

    const headers = [
      'Customer',
      'Resource',
      'Service',
      'Location',
      'Start',
      'End',
      'Price',
      'Status'
    ];

    const rows = sortedBookings.map((booking) => {
      const customerName =
        booking.metadata?.customer_name ||
        booking.customer_name ||
        booking.customer ||
        'Unknown';

      const resourceName =
        booking.resource?.name || 'Unknown';

      const serviceName =
        booking.service?.name || 'Unknown';

      const locationName =
        booking.location?.name || 'Unknown';

      const status = booking.is_canceled
        ? 'Cancelled'
        : booking.is_temporary
        ? 'Tentative'
        : 'Confirmed';

      const start = booking.starts_at
        ? new Date(booking.starts_at).toLocaleString(
            'en-US',
            {
              timeZone: 'Asia/Jakarta'
            }
          )
        : '';

      const end = booking.ends_at
        ? new Date(booking.ends_at).toLocaleString(
            'en-US',
            {
              timeZone: 'Asia/Jakarta'
            }
          )
        : '';

      return [
        `"${customerName}"`,
        `"${resourceName}"`,
        `"${serviceName}"`,
        `"${locationName}"`,
        `"${start}"`,
        `"${end}"`,
        booking.price || 0,
        status
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;'
    });

    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `cancelled_bookings_export_${
        new Date().toISOString().split('T')[0]
      }.csv`
    );

    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleBulkDelete = async () => {
    if (
      !confirm(
        `Delete ${selectedBookings.length} bookings permanently?`
      )
    ) {
      setShowBulkActions(false);
      return;
    }

    try {
      await Promise.all(
        selectedBookings.map((bookingId) =>
          fetch(
            `${API_BASE_URL}/deleteBooking/${bookingId}`,
            {
              method: 'DELETE',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getAccessToken()}`
              }
            }
          )
        )
      );

      setSelectedBookings([]);

      fetchCancelledBookings(currentPage);

      alert('Successfully deleted bookings');
    } catch (error) {
      alert('Failed to delete bookings');
    }

    setShowBulkActions(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="text-gray-500">
          Loading...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center py-12">
        <div style={{ color: '#db6969' }}>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 overflow-x-auto pb-2">
        <div className="flex items-center gap-3 min-w-max">
          <div className="relative">
            <button
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              onClick={() =>
                setShowSortDropdown(!showSortDropdown)
              }
            >
              Sort:{' '}
              {sortBy.charAt(0).toUpperCase() +
                sortBy.slice(1)}{' '}
              {sortOrder === 'asc' ? '↑' : '↓'}

              <ChevronRight
                className={`w-4 h-4 transform ${
                  showSortDropdown
                    ? 'rotate-90'
                    : ''
                }`}
              />
            </button>

            {showSortDropdown && (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                <div
                  className="absolute inset-0 bg-black bg-opacity-40 transition-opacity"
                  onClick={() =>
                    setShowSortDropdown(false)
                  }
                />

                <div className="relative bg-white rounded-xl shadow-2xl max-w-xs w-full p-5 overflow-hidden transform transition-all text-center">
                  <div className="mb-4">
                    <h3 className="text-base font-semibold text-gray-900">
                      Sort Cancelled Bookings
                    </h3>

                    <p className="text-xs text-gray-500 mt-1">
                      Choose how to order the list
                    </p>
                  </div>

                  <div className="flex flex-col gap-2">
                    {[
                      {
                        label: 'Date: Newest First',
                        value: 'date',
                        order: 'desc'
                      },
                      {
                        label: 'Date: Oldest First',
                        value: 'date',
                        order: 'asc'
                      },
                      {
                        label: 'Resource: A to Z',
                        value: 'resource',
                        order: 'asc'
                      },
                      {
                        label: 'Resource: Z to A',
                        value: 'resource',
                        order: 'desc'
                      },
                      {
                        label: 'Duration: Longest First',
                        value: 'duration',
                        order: 'desc'
                      },
                      {
                        label: 'Duration: Shortest First',
                        value: 'duration',
                        order: 'asc'
                      }
                    ].map((opt) => {
                      const isActive =
                        sortBy === opt.value &&
                        sortOrder === opt.order;

                      return (
                        <button
                          key={opt.label}
                          type="button"
                          className={`w-full px-4 py-2 text-sm font-medium rounded-lg transition-colors border ${
                            isActive
                              ? 'bg-blue-50 text-blue-600 border-blue-200'
                              : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border-gray-200'
                          }`}
                          onClick={() => {
                            setSortBy(opt.value);
                            setSortOrder(opt.order);
                            setShowSortDropdown(false);
                          }}
                        >
                          {opt.label}
                        </button>
                      );
                    })}

                    <div className="border-t border-gray-100 my-1" />

                    <button
                      type="button"
                      className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg transition-colors"
                      onClick={() =>
                        setShowSortDropdown(false)
                      }
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />

            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) =>
                setSearchTerm(e.target.value)
              }
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm w-[200px] sm:w-[300px] lg:w-[400px] xl:w-[500px]"
            />
          </div>
        </div>

        <div className="flex items-center gap-3 min-w-max">
          {selectedBookings.length > 0 && (
            <div className="relative">
              <button
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 border rounded-md hover:bg-gray-50"
                onClick={() =>
                  setShowBulkActions(!showBulkActions)
                }
              >
                Bulk Actions ({selectedBookings.length})
              </button>

              {showBulkActions && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                  <div
                    className="absolute inset-0 bg-black bg-opacity-40 transition-opacity"
                    onClick={() =>
                      setShowBulkActions(false)
                    }
                  />

                  <div className="relative bg-white rounded-xl shadow-2xl max-w-xs w-full p-5 overflow-hidden transform transition-all text-center">
                    <div className="mb-4">
                      <h3 className="text-base font-semibold text-gray-900">
                        Bulk Actions ({selectedBookings.length})
                      </h3>

                      <p className="text-xs text-gray-500 mt-1">
                        Choose an action to apply to the selected records.
                      </p>
                    </div>

                    <div className="flex flex-col gap-2">
                      <button
                        type="button"
                        className="w-full px-4 py-2.5 text-sm font-medium text-white bg-[#db6969] hover:bg-red-700 rounded-lg transition-colors shadow-sm"
                        onClick={handleBulkDelete}
                      >
                        Delete Selected
                      </button>

                      <button
                        type="button"
                        className="w-full px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors"
                        onClick={() =>
                          setShowBulkActions(false)
                        }
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 border rounded-md hover:bg-gray-50"
          >
            <Download className="w-4 h-4" />
            Export {paginatedBookings.length} records
          </button>
        </div>
      </div>

      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <div className="min-w-[900px]">
            <div className="border-b">
              <div className="flex items-center px-4 py-3 text-sm font-medium text-gray-600">
                <div className="w-8 mr-4">
                  <input
                    type="checkbox"
                    checked={
                      selectedBookings.length ===
                        paginatedBookings.length &&
                      paginatedBookings.length > 0
                    }
                    onChange={() => {
                      if (
                        selectedBookings.length ===
                        paginatedBookings.length
                      ) {
                        setSelectedBookings([]);
                      } else {
                        setSelectedBookings(
                          paginatedBookings.map(
                            (b) => b.id
                          )
                        );
                      }
                    }}
                    className="w-4 h-4 rounded"
                    style={{
                      accentColor: '#184AC0'
                    }}
                  />
                </div>

                <div className="w-16 mr-4"></div>

                <div className="flex-1 mr-4">
                  Resource
                </div>

                <div className="w-32 mr-4">
                  Price
                </div>

                <div className="w-24 mr-4">
                  Duration
                </div>

                <div className="w-24 mr-4">
                  Status
                </div>

                <div className="w-8"></div>
              </div>
            </div>

            <div>
              {paginatedBookings.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  No cancelled bookings found
                </div>
              ) : (
                paginatedBookings.map((booking) => (
                  <CancelledBookingRow
                    key={booking.id}
                    booking={booking}
                    isSelected={selectedBookings.includes(
                      booking.id
                    )}
                    onSelect={(id) =>
                      setSelectedBookings((prev) =>
                        prev.includes(id)
                          ? prev.filter(
                              (i) => i !== id
                            )
                          : [...prev, id]
                      )
                    }
                    onNavigateToDetail={(id) =>
                      navigate(`/bookings/${id}`)
                    }
                    onRefresh={fetchCancelledBookings}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center gap-4 justify-between">
          <div className="text-sm text-gray-700">
            Showing{' '}
            {totalBookings === 0
              ? 0
              : (currentPage - 1) * pageSize + 1}{' '}
            to{' '}
            {Math.min(
              currentPage * pageSize,
              totalBookings
            )}{' '}
            of {totalBookings}
          </div>

          <div className="flex gap-2">
            <button
              onClick={() =>
                setCurrentPage(
                  Math.max(1, currentPage - 1)
                )
              }
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm border rounded-md disabled:opacity-50"
            >
              Previous
            </button>

            {[...Array(Math.min(5, totalPages))].map(
              (_, i) => {
                const pageNum =
                  Math.max(
                    1,
                    Math.min(
                      totalPages - 4,
                      currentPage - 2
                    )
                  ) + i;

                return (
                  <button
                    key={pageNum}
                    onClick={() =>
                      setCurrentPage(pageNum)
                    }
                    className={`px-3 py-1 text-sm border rounded-md ${
                      currentPage === pageNum
                        ? 'bg-blue-600 text-white'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              }
            )}

            <button
              onClick={() =>
                setCurrentPage(
                  Math.min(
                    totalPages,
                    currentPage + 1
                  )
                )
              }
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-sm border rounded-md disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}